import { COMPONENT_TYPE, TAB_MANAGER, RELOAD } from '../constants';

export class TabManager {
  constructor(tabContainer, options = {}) {
    tabContainer.tabManager = this;

    const url = new URL(window.location.href);
    const pathAfterDomain = url.pathname.replace(/^\//, ''); // Убираем ведущий слэш

    this.additionalData = {};
    this.loadedTabs = {};
    this.tabContainer = tabContainer;
    this.tabLinks = this.tabContainer.querySelectorAll('.nav-link');
    this.tabPanes = this.tabContainer.querySelectorAll('.tab-pane');

    this.activeTabCookieName = pathAfterDomain + `activeTab_${this.tabContainer.id}`;

    // Check if any tab has data-active="true"
    let activeTabFromAttr = -1;
    this.tabLinks.forEach((link, index) => {
      const navItem = link.closest('.nav-item');
      if (navItem && navItem.dataset.active === 'true') {
        activeTabFromAttr = index;
      }
    });

    // Use the attribute-defined active tab if available, otherwise use cookie value
    this.activeTabIndex = activeTabFromAttr >= 0 ?
      activeTabFromAttr :
      Number(getCookie(this.activeTabCookieName, 0));

    this.activeTab = this.tabLinks[this.activeTabIndex];
    this.tabTitle = options.tabTitle ?? '';
    this.onReloadTab = options.onReloadTab;
    this.lastTabIndex = this.activeTabIndex;
    this.viewMode = 'default';
    this.archiveMode = false; // Добавляем свойство для отслеживания режима архива
    this.buttons = options.buttons || [];
    this.tabContainer.reloader = this.reloadActiveTab.bind(this);
    this._init();
  }

  setAdditionalData({ data, merge, reload = true }) {
    if (merge) this.additionalData = Object.assign(this.additionalData, merge);
    if (data) this.additionalData = data;
    if (reload) this.reloadActiveTab();
  }

  _init() {
    this._renderHeaderContainer();
    this._renderTitle();
    this._renderButtons();
    this._attachClickListener();
    this._loadTabContent(this.activeTabIndex);
    this._updateActiveTabUI();
  }

  _renderHeaderContainer() {
    if (!this.tabContainer.querySelector('.card-header-container')) {
      this.headContainer = document.createElement('div');
      this.headContainer.classList.add('card-header-container');
      this.tabContainer.querySelector('.card-header, .tab-header').appendChild(this.headContainer);
    }
  }

  _renderTitle() {
    if (!this.tabContainer.querySelector('.card-header-title')) {
      this.headTitle = document.createElement('div');
      this.headTitle.classList.add('card-header-title', 'col');
      this.headTitle.textContent = this.tabTitle;
      this.headContainer.appendChild(this.headTitle);
    }
  }

  _renderButtons() {
    // Находим контейнер для кнопок
    if (!this.tabContainer.querySelector('.card-header-buttons')) {
      // Если контейнера нет, создаем его
      this.buttonContainer = document.createElement('div');
      this.buttonContainer.classList.add('card-header-buttons');
      this.headContainer.appendChild(this.buttonContainer);
    }

    // Очищаем существующие кнопки
    this.buttonContainer.innerHTML = '';

    // Рендерим кнопки
    this.buttons.forEach((buttonConfig, index) => {
      const button = document.createElement('div');
      button.classList.add('btn', 'btn-secondary');
      if (typeof buttonConfig.label === 'function') button.textContent = buttonConfig.label.call(this);
      else button.textContent = buttonConfig.label;

      // Устанавливаем начальное активное состояние
      if (typeof buttonConfig.isActive === 'function') {
        if (buttonConfig.isActive.call(this)) {
          button.classList.add('active');
        }
      } else if (buttonConfig.isActive) {
        button.classList.add('active');
      }

      // Устанавливаем видимость
      if (typeof buttonConfig.isVisible === 'function') {
        if (!buttonConfig.isVisible.call(this)) {
          button.style.display = 'none';
        }
      } else if (buttonConfig.isVisible === false) {
        button.style.display = 'none';
      }

      // Привязываем обработчик события
      button.addEventListener('click', (e) => {
        buttonConfig.onClick.call(this, e, button, index);
        // Обновляем UI кнопок после действия
        this._updateButtonsUI();
      });

      this.buttonContainer.appendChild(button);
      // Сохраняем ссылку на элемент кнопки
      buttonConfig._element = button;
    });
  }

  _updateButtonsUI() {
    this.buttons.forEach((buttonConfig) => {
      const button = buttonConfig._element;
      // Обновляем активное состояние
      if (typeof buttonConfig.label === 'function') button.textContent = buttonConfig.label.call(this);
      if (typeof buttonConfig.isActive === 'function') {
        if (buttonConfig.isActive.call(this)) {
          button.classList.add('active');
        } else {
          button.classList.remove('active');
        }
      } else if (buttonConfig.isActive) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
      // Обновляем видимость
      if (typeof buttonConfig.isVisible === 'function') {
        if (!buttonConfig.isVisible.call(this)) {
          button.style.display = 'none';
        } else {
          button.style.display = '';
        }
      } else if (buttonConfig.isVisible === false) {
        button.style.display = 'none';
      } else {
        button.style.display = '';
      }
    });
  }

  // Прикрепление слушателей клика к вкладкам
  _attachClickListener() {
    this.tabLinks.forEach((link, index) => link.addEventListener('click', () => this._handleTabClick(link, index)));
  }

  // Обработка клика по вкладке
  _handleTabClick(tabElement, index) {
    const isReload = this.activeTabIndex === index;
    this.lastTabIndex = this.activeTabIndex;
    this.activeTabIndex = index;
    this.activeTab = this.tabLinks[index];
    setCookie(this.activeTabCookieName, index);
    this._updateActiveTabUI();

    // Загрузка содержимого вкладки
    if (typeof tabElement.dataset.href !== 'undefined') this._loadTabContent(index, isReload);
  }

  _updateActiveTabUI() {
    this.tabLinks.forEach((link, index) => {
      if (index === this.activeTabIndex) {
        link.classList.add('active');
        link.setAttribute('aria-selected', 'true');
      } else {
        link.classList.remove('active');
        link.setAttribute('aria-selected', 'false');
      }
    });

    this.tabPanes.forEach((pane, index) => {
      if (index === this.activeTabIndex) {
        pane.classList.add('active');
        pane.classList.add('show'); // Добавляем класс show для анимации
      } else {
        pane.classList.remove('active');
        pane.classList.remove('show'); // Удаляем класс show

        // При уходе с вкладки делаем первый fieldset disabled
        const firstFieldset = pane.querySelector('fieldset');
        if (firstFieldset) {
          firstFieldset.disabled = true;
        }
      }
    });
  }

  reloadActiveTab(options = {
      data = {},
      options = {},
  } = {}) {
    this.tabContainer.set(options.data)
    this.tabContainer.updateData();
    return this._loadTabContent(this.activeTabIndex, true);
  }

  async _loadTabContent(index, isReload = false) {
    const tabLink = this.tabLinks[index];
    const contentPane = this.tabPanes[index];
    const url = tabLink.dataset.href;
    const data = Object.assign(getDataAll(this.tabContainer), getDataAll(tabLink), this.additionalData);

    // Показываем спиннер только если контент не был загружен (есть класс loading-spinner)
    const hasLoadingSpinner = contentPane.querySelector('.loading-spinner');
    if (hasLoadingSpinner) {
      // Спиннер уже есть, ничего не делаем
    } else {
      // Контент уже загружен, не показываем спиннер при перезагрузке
    }

    const headers = {
      [COMPONENT_TYPE]: TAB_MANAGER,
    };

    if (isReload) {
      headers[RELOAD] = 'true';
    }

    return new SendData({
      url,
      data: Object.assign(data),
      headers: headers
    }).then((response) => {
      this.loadedTabs[index] = true;
      return insertHtmlWithScripts({
        html: response.answer,
        parent: contentPane,
        replaceParent: true
      });
    });
  }
}
